export const SUPPORTED_LANGUAGES = ['ru', 'en', 'uz', 'tg', 'ky', 'az', 'hy', 'kk', 'hi', 'it', 'ro'] as const;

export type SupportedLanguage = (typeof SUPPORTED_LANGUAGES)[number];

export const DEFAULT_FALLBACK_LANGUAGE: SupportedLanguage = 'en';

export const BACKEND_PERSISTED_LANGUAGES = ['ru', 'en', 'uz', 'tg', 'ky', 'az', 'hy'] as const;

export type BackendPersistedLanguage = (typeof BACKEND_PERSISTED_LANGUAGES)[number];

const SUPPORTED_LANGUAGES_SET = new Set<string>(SUPPORTED_LANGUAGES);
const BACKEND_PERSISTED_LANGUAGES_SET = new Set<string>(BACKEND_PERSISTED_LANGUAGES);

function toSupportedLanguageOrNull(locale: string | null | undefined): SupportedLanguage | null {
  if (!locale) return null;

  const normalized = locale.trim().toLowerCase().replace(/_/g, '-');
  if (!normalized) return null;

  const [language] = normalized.split('-');
  return isSupportedLanguage(language) ? language : null;
}

export function isSupportedLanguage(value: string | null | undefined): value is SupportedLanguage {
  return typeof value === 'string' && SUPPORTED_LANGUAGES_SET.has(value);
}

export function isBackendPersistedLanguage(value: string | null | undefined): value is BackendPersistedLanguage {
  return typeof value === 'string' && BACKEND_PERSISTED_LANGUAGES_SET.has(value);
}

export function normalizeLocaleToLanguage(locale: string | null | undefined): SupportedLanguage {
  return toSupportedLanguageOrNull(locale) ?? DEFAULT_FALLBACK_LANGUAGE;
}

type LocaleSource = string | null | undefined;

export type ResolveInitialLanguageOptions = {
  savedLanguage?: LocaleSource;
  platformLocale?: LocaleSource | readonly LocaleSource[];
  browserLocale?: LocaleSource | readonly LocaleSource[];
  fallbackLanguage?: SupportedLanguage;
};

function asLocaleList(value: LocaleSource | readonly LocaleSource[] | undefined): LocaleSource[] {
  if (typeof value === 'string' || value == null) return [value];
  return [...value];
}

export function resolveInitialLanguage(options: ResolveInitialLanguageOptions): SupportedLanguage {
  const fallbackLanguage = options.fallbackLanguage ?? DEFAULT_FALLBACK_LANGUAGE;
  const candidates = [
    options.savedLanguage,
    ...asLocaleList(options.platformLocale),
    ...asLocaleList(options.browserLocale),
  ];

  for (const candidate of candidates) {
    if (!candidate) continue;
    const normalized = toSupportedLanguageOrNull(candidate);
    if (normalized) return normalized;
  }

  return fallbackLanguage;
}

/** Parse Accept-Language header tokens in priority order (without q weights). */
export function parseAcceptLanguageHeader(header: string | null | undefined): string[] {
  if (!header?.trim()) return [];
  const tokens: string[] = [];
  for (const part of header.split(',')) {
    const language = part.trim().split(';')[0]?.trim();
    if (language) tokens.push(language);
  }
  return tokens;
}

export type ResolveLanguageFromAcceptLanguageOptions = {
  fallbackLanguage?: SupportedLanguage;
};

/** Resolve session/UI language from standard Accept-Language header. */
export function resolveLanguageFromAcceptLanguage(
  header: string | null | undefined,
  options?: ResolveLanguageFromAcceptLanguageOptions,
): SupportedLanguage {
  const fallbackLanguage = options?.fallbackLanguage ?? DEFAULT_FALLBACK_LANGUAGE;
  for (const token of parseAcceptLanguageHeader(header)) {
    const normalized = normalizeLocaleToLanguage(token);
    if (isSupportedLanguage(normalized)) return normalized;
  }
  return fallbackLanguage;
}
